import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { useNavigationContext } from '@/hooks/useNavigationHook'
import { serializeError } from '@/monitoring/diagnostics'
import { AuthService } from '@/services/authService'
import { deviceTokenService } from '@/services/deviceTokenService'
import { POSService } from '@/services/posService'
import { Auth } from '@/types/common/httpRequest'
import WelcomeWithServicesPage from './welcomeWithServicesPage'

function HomePage() {
    const navigate = useNavigate()
    const { startUrl } = useNavigationContext()

    useEffect(() => {
        let cancelled = false

        const bootstrap = async () => {
            const bootstrapResult = await deviceTokenService.initialize()
            if (cancelled) return

            if (bootstrapResult === 'connectivity_retry') {
                console.warn(
                    'Device token bootstrap requires connectivity recovery - redirecting to connectivity issues',
                    { bootstrapResult }
                )
                navigate('/connectivity-issues', {
                    state: {
                        source: 'homePage.bootstrap',
                        reason: `Token bootstrap: ${bootstrapResult} (localStorage empty or device API unreachable/empty with prior registration)`
                    }
                })
                return
            }

            if (cancelled) return

            if (bootstrapResult === 'register') {
                console.warn(
                    'Device token bootstrap: no token in localStorage or device API, and no prior registration marker - redirecting to registration'
                )
                navigate('/register', {
                    state: {
                        source: 'homePage.bootstrap',
                        reason: 'No device token in localStorage or device API, and no vcash_device_was_registered marker'
                    }
                })
                return
            }

            try {
                console.log('Attempting to create session...')
                await POSService.createSession()
                if (cancelled) return
                console.log(
                    '✅ Session created successfully - redirecting to welcome'
                )
                navigate(startUrl ?? '/welcome')
            } catch (error) {
                if (cancelled) return
                if (
                    error &&
                    typeof error === 'object' &&
                    'requiresNavigation' in error
                ) {
                    const navigationError = error as {
                        requiresNavigation: string
                        navigationState?: unknown
                    }
                    console.log(
                        '🔄 API error requires navigation to:',
                        navigationError.requiresNavigation
                    )
                    navigate(
                        navigationError.requiresNavigation,
                        navigationError.navigationState
                            ? { state: navigationError.navigationState }
                            : undefined
                    )
                } else if (AuthService.HasToken(Auth.POS)) {
                    console.error(
                        'Failed to create session with device token in localStorage - redirecting to connectivity issues:',
                        error
                    )
                    navigate('/connectivity-issues', {
                        state: {
                            source: 'homePage.createSession',
                            reason: 'POSService.createSession() failed while a device token is present (may be stale; will reload from device API on recovery page)',
                            error: serializeError(error)
                        }
                    })
                } else {
                    console.error(
                        'Failed to create session with no device token - redirecting to registration:',
                        error
                    )
                    navigate('/register')
                }
            }
        }

        bootstrap()

        return () => {
            cancelled = true
        }
    }, [navigate, startUrl])

    return <WelcomeWithServicesPage />
}

export default HomePage
